import { Bound, Direction, Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { createPosition, isInBounds } from '@/shared/ui/data/EditableGrid/lib/position';

export type NextBy =
  | {
      type: 'direction';
      direction: Direction;
    }
  | {
      type: 'tab';
      shiftKeyPressed: boolean;
    }
  | {
      type: 'enter';
    };

export function getNextPosition(current: Position, bound: Bound, nextBy: NextBy): Position {
  switch (nextBy.type) {
    case 'direction':
      return getNextPositionByDirection(current, nextBy.direction, bound);
    case 'tab':
      return getNextPositionByTab(current, bound, nextBy.shiftKeyPressed);
    case 'enter':
      return getNextPositionByEnter(current, bound);
  }
}

function getNextPositionByDirection(
  current: Position,
  direction: Direction,
  bound: Bound
): Position {
  let nextPosition: Position = current;

  switch (direction) {
    case 'up':
      nextPosition = prevRow(current, bound);
      break;
    case 'down':
      nextPosition = nextRow(current, bound);
      break;
    case 'left':
      nextPosition = leftCol(current, bound);
      break;
    case 'right':
      nextPosition = rightCol(current, bound);
      break;
  }

  if (isInBounds(nextPosition, bound)) return nextPosition;
  else return current;
}

function getNextPositionByTab(
  current: Position,
  bound: Bound,
  shiftKeyPressed: boolean = false
): Position {
  if (shiftKeyPressed) {
    const isFirstCol = current.colIndex === 0;
    const isFirstRow = current.rowIndex === 0;

    if (!isFirstCol) return leftCol(current, bound);
    else if (!isFirstRow) return prevRowLastCol(current, bound);
  } else {
    const isLastCol = current.colIndex >= bound.colCount - 1;
    const isLastRow = current.rowIndex >= bound.rowCount - 1;

    if (!isLastCol) return rightCol(current, bound);
    else if (!isLastRow) return nextRowFirstCol(current, bound);
  }

  return current;
}

function getNextPositionByEnter(current: Position, bound: Bound): Position {
  return getNextPositionByDirection(current, 'down', bound);
}

function prevRow(current: Position, bound: Bound): Position {
  const prevRowIndex = Math.max(0, current.rowIndex - 1);
  return createPosition(prevRowIndex, current.colIndex);
}

function nextRow(current: Position, bound: Bound): Position {
  const nextRowIndex = Math.min(bound.rowCount - 1, current.rowIndex + 1);
  return createPosition(nextRowIndex, current.colIndex);
}

function leftCol(current: Position, bound: Bound): Position {
  const leftColIndex = Math.max(0, current.colIndex - 1);
  return createPosition(current.rowIndex, leftColIndex);
}

function rightCol(current: Position, bound: Bound): Position {
  const rightColIndex = Math.max(0, current.colIndex + 1);
  return createPosition(current.rowIndex, rightColIndex);
}

function firstCol(current: Position, bound: Bound): Position {
  return createPosition(current.rowIndex, 0);
}

function lastCol(current: Position, bound: Bound): Position {
  const lastColIndex = Math.max(0, bound.colCount - 1);
  return createPosition(current.rowIndex, lastColIndex);
}

function prevRowLastCol(current: Position, bound: Bound): Position {
  return prevRow(lastCol(current, bound), bound);
}

function nextRowFirstCol(current: Position, bound: Bound): Position {
  return nextRow(firstCol(current, bound), bound);
}
